import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import {
  decodeCrmContactList,
  decodeCrmDonorDetail,
  decodeCrmDonorList,
  decodeCrmImportedContact,
  decodeCrmImportedContactList,
  decodeCrmSegmentDetail,
  decodeCrmSegmentList,
  decodeCrmSegmentMembers,
  decodeCrmSegmentOptions,
} from './crm-decode';
import {
  evaluateCrmSegment,
  fetchCrmContacts,
  fetchCrmDonor,
  fetchCrmDonors,
  fetchCrmImportedContact,
  fetchCrmImportedContacts,
  fetchCrmSegment,
  fetchCrmSegmentMembers,
  fetchCrmSegmentOptions,
  fetchCrmSegments,
} from './crm-transport';
import type { CrmSegmentDefinition } from './wire-types';

export const crmKeys = {
  all: ['org-crm'] as const,
  org: (orgRef: string) => [...crmKeys.all, orgRef] as const,
  donors: (orgRef: string) => [...crmKeys.org(orgRef), 'donors'] as const,
  donor: (orgRef: string, donorId: string) => [...crmKeys.donors(orgRef), donorId] as const,
  imported: (orgRef: string, q: string, limit: number, offset: number) =>
    [...crmKeys.org(orgRef), 'imported-contacts', q, limit, offset] as const,
  importedContact: (orgRef: string, contactId: string) =>
    [...crmKeys.org(orgRef), 'imported-contact', contactId] as const,
  contacts: (orgRef: string, q: string, limit: number, offset: number) =>
    [...crmKeys.org(orgRef), 'contacts', q, limit, offset] as const,
  segments: (orgRef: string) => [...crmKeys.org(orgRef), 'segments'] as const,
  segmentOptions: (orgRef: string) => [...crmKeys.segments(orgRef), 'options'] as const,
  segment: (orgRef: string, segmentId: string) =>
    [...crmKeys.segments(orgRef), 'one', segmentId] as const,
  segmentMembers: (orgRef: string, segmentId: string, limit: number, offset: number) =>
    [...crmKeys.segment(orgRef, segmentId), 'members', limit, offset] as const,
  evaluation: (orgRef: string, definition: string, limit: number, offset: number) =>
    [...crmKeys.segments(orgRef), 'evaluate', definition, limit, offset] as const,
};

export function crmDonorsOptions(orgRef: string) {
  return queryOptions({
    queryKey: crmKeys.donors(orgRef),
    queryFn: ({ signal }) => fetchCrmDonors(orgRef, { signal }),
    select: decodeCrmDonorList,
    retry: false,
  });
}

export function crmDonorOptions(orgRef: string, donorId: string) {
  return queryOptions({
    queryKey: crmKeys.donor(orgRef, donorId),
    queryFn: ({ signal }) => fetchCrmDonor(orgRef, donorId, { signal }),
    select: decodeCrmDonorDetail,
    retry: false,
  });
}

export function crmImportedContactsOptions(
  orgRef: string,
  read: { q: string; limit: number; offset: number },
) {
  return queryOptions({
    queryKey: crmKeys.imported(orgRef, read.q, read.limit, read.offset),
    queryFn: ({ signal }) =>
      fetchCrmImportedContacts(orgRef, {
        ...(read.q.length > 0 ? { q: read.q } : {}),
        limit: read.limit,
        offset: read.offset,
        signal,
      }),
    select: decodeCrmImportedContactList,
    retry: false,
  });
}

/** One of the organization's own contacts: the contact panel's read. */
export function crmImportedContactOptions(orgRef: string, contactId: string) {
  return queryOptions({
    queryKey: crmKeys.importedContact(orgRef, contactId),
    queryFn: ({ signal }) => fetchCrmImportedContact(orgRef, contactId, { signal }),
    select: decodeCrmImportedContact,
    retry: false,
  });
}

// ── CRM-5: the whole list and segments ────────────────────────────────────────────────────────────

export function crmContactsOptions(
  orgRef: string,
  read: { q: string; limit: number; offset: number },
) {
  return queryOptions({
    queryKey: crmKeys.contacts(orgRef, read.q, read.limit, read.offset),
    queryFn: ({ signal }) =>
      fetchCrmContacts(orgRef, {
        ...(read.q.length > 0 ? { q: read.q } : {}),
        limit: read.limit,
        offset: read.offset,
        signal,
      }),
    select: decodeCrmContactList,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function crmSegmentsOptions(orgRef: string) {
  return queryOptions({
    queryKey: crmKeys.segments(orgRef),
    queryFn: ({ signal }) => fetchCrmSegments(orgRef, { signal }),
    select: decodeCrmSegmentList,
    retry: false,
  });
}

export function crmSegmentConditionOptions(orgRef: string) {
  return queryOptions({
    queryKey: crmKeys.segmentOptions(orgRef),
    queryFn: ({ signal }) => fetchCrmSegmentOptions(orgRef, { signal }),
    select: decodeCrmSegmentOptions,
    retry: false,
  });
}

export function crmSegmentDetailOptions(orgRef: string, segmentId: string) {
  return queryOptions({
    queryKey: crmKeys.segment(orgRef, segmentId),
    queryFn: ({ signal }) => fetchCrmSegment(orgRef, segmentId, { signal }),
    select: decodeCrmSegmentDetail,
    retry: false,
  });
}

export function crmSegmentMembersOptions(
  orgRef: string,
  segmentId: string,
  read: { limit: number; offset: number },
) {
  return queryOptions({
    queryKey: crmKeys.segmentMembers(orgRef, segmentId, read.limit, read.offset),
    queryFn: ({ signal }) => fetchCrmSegmentMembers(orgRef, segmentId, { ...read, signal }),
    select: decodeCrmSegmentMembers,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** What an unsaved definition holds now. Keyed by the definition's JSON, so each edit is one read. */
export function crmSegmentEvaluationOptions(
  orgRef: string,
  definition: CrmSegmentDefinition,
  read: { limit: number; offset: number },
) {
  return queryOptions({
    queryKey: crmKeys.evaluation(orgRef, JSON.stringify(definition), read.limit, read.offset),
    queryFn: ({ signal }) => evaluateCrmSegment(orgRef, { definition }, { ...read, signal }),
    select: decodeCrmSegmentMembers,
    placeholderData: keepPreviousData,
    retry: false,
  });
}
